import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { useUnresolvedAlerts } from "@/shared/api/hooks/alerts";
import { useSites } from "@/shared/api/hooks/sites";
import { qk } from "@/shared/api/keys";
import { useOrgAlertChannel } from "@/shared/api/ws";
import { selectUser, useAuth } from "@/shared/auth/store";
import { useAlertTTS } from "@/shared/hooks/useAlertTTS";
import { isUnresolved } from "@/shared/lib/alertScope";

/**
 * 한 번에 발화할 수 있는 경보 수. 낭독은 <b>전역 FIFO 큐 한 개</b>이고(useAlertTTS
 * `broadcastQueue`), 한 건이 선택된 언어 수만큼 반복되며 항목마다 수 초가 걸린다.
 * 큐에 12건을 밀어 넣으면 그 뒤에 <b>진짜로 새로 터진 경보</b>가 수 분간 발화되지 못한다.
 * 그래서 개수로 제한한다 — 「언제 발생했나」는 묻지 않으므로 아직 새고 있는 오래된
 * 경보를 죽이지 않는다.
 */
const MAX_ANNOUNCE_PER_TICK = 3;

/**
 * Headless app-wide announcer. Mounted once in <AppShell> so new alerts
 * are read aloud (when TTS is enabled) on every page, not just the alerts
 * view.
 *
 * Source of truth is `useUnresolvedAlerts()` — 미해제 경보 전체(무필터 1페이지 +
 * state=open/acknowledged 각 500건의 합집합), 30초 폴링. 무필터 1페이지만 보면
 * 경보가 몰릴 때 아직 해제되지 않은 경보가 50건 밖으로 밀려 목록에서 사라지고,
 * 그러면 음성도 같이 사라진다.
 *
 * 여기에 더해 조직 경보 채널을 구독해 새 경보가 발령되는 즉시 그 질의를 무효화한다.
 * 서버가 실제로 발행하는 이벤트는 `alert.fired` 하나뿐이고(safety/tasks.py),
 * <b>해제(cleared)를 실시간으로 알리는 이벤트는 없다</b> — 해제 반영은 30초 폴링이
 * 담당한다. Dedupe lives in `useAlertTTS` (per alert id), so re-renders
 * and overlapping mounts never double-speak.
 */
export function AlertTtsAnnouncer() {
  const { announce } = useAlertTTS();
  const qc = useQueryClient();
  const user = useAuth(selectUser);
  const orgId = user?.organization?.id ?? null;

  const { alerts, ready } = useUnresolvedAlerts();
  const { data: sitesData } = useSites();

  const siteName = useMemo(() => {
    const m = new Map<number, string>();
    (sitesData?.results ?? []).forEach((s) => {
      if (s.id != null) m.set(s.id, s.name);
    });
    return (id: number | string) => m.get(Number(id)) ?? "";
  }, [sitesData]);

  /**
   * 이 세션이 시작될 때 <b>이미 목록에 있던</b> 경보 id — <b>기준선</b>. 「경보 목록을 처음
   * 받았다」는 「경보가 지금 발령됐다」가 아니므로 읽지 않고 여기에만 담는다. 이후 목록에
   * 새로 나타나는 id 만 발화 후보가 된다.
   *
   * <p>⚠️ 2026-08-31 사고: 음성경보 기본값을 켜자 화면을 열 때마다 <b>과거 경보 12건이 줄줄이
   * 낭독</b>됐다. 며칠 전(7월) 것까지 포함이었다. 원인은 이 컴포넌트가 목록이 바뀔 때마다
   * <b>목록 전체</b>를 넘기고, 중복방지 Set 은 훅 안에 있어 <b>새로고침하면 비기</b> 때문이다.
   * 「경보가 발령됐다」와 「경보 목록을 처음 받았다」는 전혀 다른 사건인데 구분이 없었다.
   * <b>그 사고를 막는 것은 이 기준선이다</b> — 첫 응답은 통째로 흡수되어 한 건도 발화되지
   * 않는다. 여기에 「한 번에 최대 {@link MAX_ANNOUNCE_PER_TICK}건」이라는 개수 상한이
   * 두 번째 벨트로 걸린다.</p>
   *
   * <p>⚠️ 함께 있던 「발생 후 10분 이내만 읽는다」는 시간창은 <b>제거했다</b>. 미해제 경보는
   * 오래됐다고 덜 위험하지 않다 — 폴링이 40분 멈췄다 복귀하면 35분 전에 시작돼 <b>아직 새고
   * 있는</b> 누출이 창 밖이라는 이유로 침묵하고, 그대로 기준선에 흡수되어 영구히 안 읽혔다.
   * 게다가 그 판정은 <b>기기 시계</b>에 걸려 있었다: 시계가 20분 빠르면 모든 경보가 창 밖이라
   * 음성 경보가 통째로 영구 침묵하고, 느리면 부호가 뒤집혀 백로그까지 전부 낭독한다(=위
   * 사고의 재현). RTC 없는 키오스크·RPi 콘솔이 실제 배치 대상이라 이론이 아니다.
   * 그래서 이 컴포넌트는 발화 여부 판정에 <b>시각을 일절 쓰지 않는다</b>.</p>
   */
  const seen = useRef<Set<number> | null>(null);

  // Push newly appeared unresolved alerts into the announcer.
  useEffect(() => {
    // 질의가 최초 1회 끝나기 전에는 아무것도 하지 않는다. ⚠️ 예전에는 `!alerts.length` 로
    // 되돌아갔는데, 그러면 경보 0건인 상태로 앱을 켰을 때 기준선이 잡히지 않고 그 뒤
    // <b>처음 터진 진짜 경보 1건이 기준선으로 흡수되어</b> 영구 침묵했다.
    if (!ready) return;

    const items = alerts
      .filter((a) => a.id != null && isUnresolved(a))
      .map((a) => ({
        id: a.id!,
        level: a.level ?? 0,
        site: a.site,
        gas: a.gas,
      }));

    if (seen.current === null) {
      // 목록이 비어 있어도 여기서 기준선을 확정한다(빈 Set).
      seen.current = new Set(items.map((i) => i.id));
      return;
    }

    const fresh = items.filter((i) => !seen.current!.has(i.id));
    if (!fresh.length) return;

    // ⛔ 기준선(`seen`)은 **실제로 읽은 것**에만 찍는다. 상한 판정 **전에** fresh 전부를
    //    찍으면, 상한에 잘린 건이 다시는 후보가 되지 않아 **영구히 안 읽힌다**
    //    (2026-09-03 검증에서 3개 렌즈가 독립적으로 같은 지적을 했다).
    //    「이미 읽었다」와 「이번엔 순서가 밀렸다」는 다른 사건이다.
    //    잘린 건은 미해제인 한 다음 폴링 목록에 그대로 남으므로 재시도가 성립한다.

    // 발화 대상이 아닌 등급은 지금 확정한다 — 다시 후보가 될 이유가 없다.
    for (const i of fresh) if (i.level < 2) seen.current!.add(i.id);

    // 같은 문장이 되는 건은 한 번만 읽는다. `buildAlertPhrase` 인자가 (등급·가스·현장)
    // 뿐이라 이 셋이 같으면 발화 문장이 글자 그대로 같다 — 12개 지점이 한 현장에서
    // 동시에 울려도 발화는 1건이다(노드는 문장에 들어가지 않으므로 손실이 없다).
    const byPhrase = new Map<string, (typeof fresh)[number][]>();
    for (const i of fresh) {
      if (i.level < 2) continue;
      const key = `${i.level}|${i.gas ?? ""}|${i.site ?? ""}`;
      const g = byPhrase.get(key);
      if (g) g.push(i);
      else byPhrase.set(key, [i]);
    }

    // 심각도 높은 순. 동급은 `alerts` 의 순서(최신순)가 그대로 유지된다(안정 정렬).
    const groups = [...byPhrase.values()].sort((a, b) => b[0].level - a[0].level);
    const batch = groups.slice(0, MAX_ANNOUNCE_PER_TICK);

    // 읽기로 한 문장에 속한 건만 기준선에 넣는다. 잘린 문장은 남겨 다음 틱에서 다시 읽는다.
    for (const g of batch) for (const i of g) seen.current!.add(i.id);
    if (batch.length) announce(batch.map((g) => g[0]), siteName);
  }, [alerts, ready, announce, siteName]);

  // Refresh the alert list the moment a new alert fires server-side.
  // ⚠️ 예전에는 `useAlertChannel(orgId, …)` 였는데, 그 훅은 `site:<id>:alerts` 채널을
  // 만든다(shared/api/ws.ts). 조직 id 를 사이트 채널에 꽂은 것이라 서버가 4403 으로
  // 끊었고 재접속도 막혀(4403 은 백오프 제외) 이 실시간 갱신은 죽어 있었다.
  // 조직 채널 훅으로 바꾼다. 이 낭독기는 WS 없이 30초 폴링만으로도 성립한다.
  useOrgAlertChannel(orgId, (env) => {
    // 서버는 `alert.fired` 만 발행한다(safety/tasks.py). 해제 반영 경로는 폴링뿐이다.
    if (env.type === "alert.fired") {
      qc.invalidateQueries({ queryKey: qk.safety.alerts.all });
    }
  });

  return null;
}
